import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * One column in a custom report's table, stored in the `columns` JSON of a
 * {@link CustomReportDefinitionEntity}. The `key` must match a column alias the
 * report's SQL selects. Keys for gross-profit values intentionally carry a `gp`
 * token so the global GpStripInterceptor blanks them for users without
 * `orders:gp:view`, exactly like the built-in reports.
 */
export interface CustomReportColumnModel {
	/** Matches a SELECT alias in the report's SQL. */
	key: string;
	/** Header label (run through t() on the frontend). */
	label: string;
	/** Optional column alignment. */
	align?: 'left' | 'center' | 'right';
	/**
	 * Number-formatting config. Loosely typed here as a record to avoid a
	 * shared→core dependency, but interpreted by the frontend renderer as
	 * `FormatOptions` from `@supply-mover-2-0/core` and passed to the shared
	 * `formatNumberWithFormatOptions` (the same formatter the list-builder uses).
	 * Omit for plain text columns (rendered via the translation function).
	 * Example: `{ numberFormat: 'currency', decimalPlaces: 2 }` or
	 * `{ numberFormat: 'percent', decimalPlaces: 4, trimTrailingZeros: true }`.
	 */
	format?: Record<string, unknown>;
	/**
	 * Renders this column's text as an in-app link. The template is an
	 * app-relative path with `{{<rowKey>}}` placeholders resolved from the same
	 * row (the referenced keys need not be declared columns — the report's SQL
	 * can select them purely to build the link), e.g.
	 * `/price-contracts/{{priceContractId}}/items?f=itemSearch:{{sku}}`.
	 *
	 * Each substituted value is URL-encoded. A row that leaves any referenced
	 * key null/empty renders as plain text, which is how a UNION report mixes
	 * linkable and non-linkable rows in one column. Resolved paths must be
	 * app-relative (a single leading `/`) — the renderer refuses anything else
	 * so a definition can never emit an off-site or `javascript:` destination.
	 */
	linkTemplate?: string;
	/**
	 * Ordered link-template candidates for a column whose rows link to DIFFERENT
	 * destinations — a UNION report's source column may point contract rows at
	 * the contract and promo rows at the promo. Each row uses the first template
	 * whose placeholders all resolve (rows carry one id and leave the others
	 * null, so exactly one candidate survives); none resolving renders plain
	 * text. Takes precedence over `linkTemplate` when both are set.
	 */
	linkTemplates?: Array<string>;
	/**
	 * Secondary values rendered as muted lines UNDER this column's value, inside
	 * the same cell — e.g. an item column showing the description with the SKU,
	 * NDC and manufacturer stacked beneath it, the way the Items list does.
	 *
	 * Each entry names a row key (which need not be a displayed column), an
	 * optional short label prefixed to the value (useful when two stacked values
	 * are both bare digit strings, like SKU and NDC), and an optional number
	 * `format` (same loose FormatOptions shape as the column's own `format`) so
	 * a stacked price renders as currency rather than a raw decimal. Rows where
	 * the key is null/empty simply omit that line. Stacking exists to save
	 * horizontal space — a wide report rendered as a dashboard widget is
	 * unreadable if every datum needs its own column (PD-13620).
	 */
	stack?: Array<{
		key: string;
		label?: string;
		format?: Record<string, unknown>;
	}>;
	/**
	 * Include this column in the report's totals row. Only `sum` is supported:
	 * the frontend sums the column over ALL rows (not just the current page)
	 * and renders a sticky totals row formatted with the column's `format`.
	 * Omit on columns where a plain sum is meaningless (e.g. percentages).
	 * Any column declaring this makes the totals row appear.
	 */
	total?: 'sum';
}

/**
 * One selectable option for a {@link CustomReportFilterModel} of type `select`
 * or `multiselect`.
 */
export interface CustomReportFilterOptionModel {
	value: string;
	label: string;
}

/**
 * A configurable, report-specific filter — beyond the built-in date-range,
 * company, and salesperson-scope filters every custom report already has.
 *
 * The frontend renders one control per filter; the engine injects a SQL fragment
 * into the report's `sqlTemplate` at the matching `{{FILTER:<key>}}` placeholder,
 * binding the selected value to the named param `:<key>`. When no value is
 * selected (e.g. the "All" option) the placeholder collapses to empty.
 *
 * `multiselect` behaves identically except the bound value is an array, which
 * the query builder expands into an IN-list — so its `sql` fragment must use
 * `IN (:<key>)`. An empty selection disables the filter exactly like the "All"
 * option on a `select`, rather than matching nothing.
 *
 * `sql` is server-only and never sent to the client.
 */
export interface CustomReportFilterModel {
	/** Filter id — also the named SQL param `:<key>` and the `{{FILTER:<key>}}` token. */
	key: string;
	/** Control label (run through t() on the frontend). */
	label: string;
	/** Control type: a single-choice dropdown or a multi-choice picker. */
	type: 'select' | 'multiselect';
	/**
	 * Dynamic option source the engine resolves into concrete options when
	 * serving the definition. `rbacRoles` lists the tenant's RBAC roles
	 * (value = role name); `ownerships` lists the tenant's chain/group
	 * ownerships (value = the ownership's external chain/group id, label =
	 * its name — PD-15234). Mutually exclusive with `options`.
	 */
	optionsSource?: 'rbacRoles' | 'ownerships';
	/** Static options (alternative to `optionsSource`). */
	options?: Array<CustomReportFilterOptionModel>;
	/**
	 * Default selection applied when the request doesn't specify one. A
	 * `multiselect` uses the array form; `select` uses the scalar form.
	 */
	defaultValue?: string | Array<string> | null;
	/**
	 * When true, offer an "All" choice (empty value) that disables the filter.
	 * Only meaningful for `select` — a `multiselect` disables itself when the
	 * user clears every option.
	 */
	includeAllOption?: boolean;
	/** Label for the "All" choice (defaults to "All"). */
	allOptionLabel?: string;
	/**
	 * SQL fragment injected at `{{FILTER:<key>}}` when a value is selected. Must
	 * reference the selected value via the named param `:<key>` (bound safely) —
	 * `= :<key>` for a `select`, `IN (:<key>)` for a `multiselect`. Authored by
	 * the dev team; never user input. SERVER-ONLY — stripped from the client
	 * response.
	 */
	sql: string;
}

/**
 * The client-safe shape of a filter (everything except the server-only `sql`),
 * returned to the frontend so it can render the control.
 */
export type CustomReportFilterClientModel = Omit<
	CustomReportFilterModel,
	'sql'
>;

/**
 * A tenant-specific dynamic report definition.
 *
 * Each row fully describes one custom report: its registry metadata, the column
 * spec the generic frontend renders, and a read-only SQL template the backend
 * engine executes. The template contains three placeholders the engine fills in
 * before running, so every custom report gets the same date-range, company, and
 * salesperson data-scope enforcement as the built-in reports:
 *  - `{{DATE_RANGE}}`        → `<dateColumn> >= :startDate AND <dateColumn> <= :endDate`
 *  - `{{COMPANY_FILTER}}`    → ` AND <companyColumn> IN (:validCompanyIds)` (or empty)
 *  - `{{SALESPERSON_SCOPE}}` → the reporting:data:* owner-scope fragment for `clientAlias`
 *
 * Definitions are authored by the dev team (never user input) and seeded
 * per-tenant, which is what makes a report tenant-specific without shipping
 * per-report code to every tenant.
 */
@Index('idx_custom_report_active', ['isActive'], {})
@Index('idx_custom_report_category', ['category'], {})
@Index('reportKey', ['reportKey'], { unique: true })
@Entity('custom_report_definition')
export class CustomReportDefinitionEntity extends BaseEntity {
	@Column('varchar', { name: 'reportKey', unique: true, length: 100 })
	public reportKey!: string;

	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('varchar', { name: 'category', length: 50 })
	public category!: string;

	/**
	 * RBAC action string checked (exact match) against the caller's permission
	 * set to gate this report. Stored as a plain string (not a shared RbacActions
	 * constant) because tenant-specific report permissions live only in the
	 * tenant DB, not in shipped code.
	 *
	 * CONVENTION: use `reporting:custom:view:<reportKey>`. Two things rely on this
	 * shape: (1) RbacService.getActions surfaces it so it's grantable in the RBAC
	 * UI; (2) the CRM nav shows the "Custom Reports" section via
	 * PermissionService.canAnyMatching('reporting:custom:view:'), so any action
	 * under that prefix reveals the section.
	 */
	@Column('varchar', { name: 'permissionAction', length: 150 })
	public permissionAction!: string;

	/**
	 * Column expression for the {{DATE_RANGE}} placeholder, e.g. `inv.date`.
	 *
	 * Null (or empty) means the report has NO timeframe dimension: the engine
	 * substitutes an always-true fragment, stops requiring the {{DATE_RANGE}}
	 * token in the template, and the frontend hides the date-range control. A
	 * report that DOES declare a date column still has to carry the token, so a
	 * date-bounded report can't accidentally scan all history.
	 */
	@Column('varchar', { name: 'dateColumn', nullable: true, length: 100 })
	public dateColumn!: string | null;

	/**
	 * Column expression for the {{COMPANY_FILTER}} placeholder, e.g.
	 * `inv.fk_company__id`. Null disables company filtering for this report.
	 */
	@Column('varchar', { name: 'companyColumn', nullable: true, length: 100 })
	public companyColumn!: string | null;

	/**
	 * Client table alias the {{SALESPERSON_SCOPE}} fragment scopes on, e.g. `c`.
	 * Null disables salesperson data-scope (report shows all rows to permitted
	 * users). Ignored when `scopePrimaryColumn` is set.
	 */
	@Column('varchar', { name: 'clientAlias', nullable: true, length: 32 })
	public clientAlias!: string | null;

	/**
	 * Optional owner-id column EXPRESSION for the {{SALESPERSON_SCOPE}}
	 * fragment's PRIMARY dimension, e.g.
	 * `COALESCE(o._fk_user__id, c._fk_user__id)`. When set it takes precedence
	 * over `clientAlias`, so a report whose displayed salesperson is NOT simply
	 * the client owner (e.g. the order-level salesperson with a client-owner
	 * fallback — PD-14039) filters on the same expression it displays. The
	 * "Unassigned" dimension tests this expression IS NULL.
	 */
	@Column('varchar', {
		name: 'scopePrimaryColumn',
		nullable: true,
		length: 255,
	})
	public scopePrimaryColumn!: string | null;

	/**
	 * Optional owner-id column expression for the ALTERNATE dimension when
	 * `scopePrimaryColumn` is set, e.g. `c._fk_user__id_alternate`. Null folds
	 * alternate-scope user ids into the primary match (see
	 * buildOwnerScopeSqlFragment).
	 */
	@Column('varchar', {
		name: 'scopeAlternateColumn',
		nullable: true,
		length: 255,
	})
	public scopeAlternateColumn!: string | null;

	@Column('json', { name: 'columns' })
	public columns!: Array<CustomReportColumnModel>;

	/**
	 * Optional report-specific filters (see {@link CustomReportFilterModel}). Each
	 * declares a `{{FILTER:<key>}}` placeholder the engine fills in the SQL. Null /
	 * empty means the report has only the built-in date/company/scope filters.
	 */
	@Column('json', { name: 'filters', nullable: true })
	public filters!: Array<CustomReportFilterModel> | null;

	/** The read-only SELECT template. Named `sqlTemplate` to avoid the SQL token. */
	@Column('text', { name: 'sqlTemplate' })
	public sqlTemplate!: string;

	/**
	 * When true, the report returns ZERO rows to any caller lacking
	 * `items:net_cost:view` (PD-13620).
	 *
	 * The global response interceptor already deletes net-cost-named keys from
	 * every response, but that only blanks a column — the rows themselves still
	 * render. A report whose entire premise is a net-cost comparison leaks the
	 * cost position through its row set alone (the mere presence of an item on a
	 * "below net cost" list says where its cost sits), so it needs the stronger
	 * all-or-nothing gate. Kept as a reusable flag rather than a hardcoded report
	 * key so any future cost-derived custom report can opt in.
	 */
	@Column('tinyint', {
		name: 'requiresNetCost',
		nullable: true,
		width: 1,
		default: () => "'0'",
		// A tinyint(1) hydrates as the NUMBER 0/1 while this property is declared
		// boolean, and the gate tests `requiresNetCost === true` — `1 === true` is
		// false, so without this the strongest gate on the report would silently
		// never fire and an unpermitted caller would receive every row. Transform
		// on read so the declared type is the type callers actually get (same
		// treatment as ClientLicenseEntity.isPrimary).
		transformer: {
			to: (value: boolean | null): number => (value === true ? 1 : 0),
			from: (value: number | null): boolean => value === 1,
		},
	})
	public requiresNetCost!: boolean | null;

	/**
	 * Optional cap on the number of rows returned when this report is rendered as
	 * a DASHBOARD WIDGET. Null (or any non-positive value) means no cap.
	 *
	 * An alert-style report can legitimately match tens of thousands of rows. The
	 * standalone report page handles that — it paginates, and it is where the list
	 * is actually worked through, so it stays exhaustive. A widget cannot: it is a
	 * small glanceable card that would pay the full payload and render cost to
	 * show a handful of lines. Capping the widget keeps it cheap while leaving the
	 * report itself complete.
	 *
	 * Two things make a cap safe to set:
	 *  - The definition's SQL must carry a deterministic ORDER BY, so the retained
	 *    window is a meaningful "worst N" rather than an arbitrary subset.
	 *  - Wherever a cap is in force the renderer states the shown count against
	 *    the true matched total, so a capped widget can never read as the complete
	 *    list.
	 *
	 * Declarative on the definition rather than keyed to a report id, so any
	 * tenant's custom report can opt in without engine changes.
	 */
	@Column('int', { name: 'widgetMaxRows', nullable: true })
	public widgetMaxRows!: number | null;

	@Column('tinyint', {
		name: 'isActive',
		nullable: true,
		width: 1,
		default: () => "'1'",
	})
	public isActive!: boolean | null;

	@Column('varchar', {
		name: 'surface',
		length: 16,
		default: () => "'CRM'",
	})
	public surface!: 'CRM' | 'STOREFRONT';
}
